/**
 * Group multi-selection component for shift forms
 * Allows selecting multiple groups using EnhancedMultiSelect with avatars and childcare info
 */

import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { EnhancedMultiSelect } from '@/components/ui/enhanced-multiselect';
import type { EnhancedSelectOption } from '@/components/ui/enhanced-multiselect';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useGroups } from '@/hooks/useGroups';
import { useChildcares } from '@/hooks/settings/useChildcare';
import { useImageUrl } from '@/hooks/useImage';
import { isImageId, isDirectImageUrl } from '@/utils/imageUtils';
import { Loader2 } from 'lucide-react';

export interface GroupMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  id?: string;
  'aria-describedby'?: string;
  /** Only these groups are offered (a group lead's groups, #2043). Absent: every group. */
  allowedGroupIds?: readonly string[];
}

interface GroupData {
  id?: string;
  name?: string;
  avatarUrl?: string;
  childcare_id?: string;
  color?: string;
}

interface Childcare {
  id: string;
  name: string;
}

/**
 * Helper component to render group avatar with image URL resolution
 */
function GroupAvatar({ group }: { group: GroupData }) {
  const avatarUrlRaw = group.avatarUrl;
  const avatarUrl = avatarUrlRaw?.trim() || undefined;

  // Check if avatarUrl is an image ID that needs resolution
  const needsImageResolution = isImageId(avatarUrl);

  // Use the useImageUrl hook to resolve image ID to URL if needed
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  // Determine the final avatar URL to use
  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined;
  }

  const initials = group.name ? group.name.substring(0, 2).toUpperCase() : '';

  return (
    <Avatar
      className="h-6 w-6 flex-shrink-0"
      style={{
        backgroundColor: group.color ? `${group.color}20` : undefined,
        borderColor: group.color || undefined,
        borderWidth: group.color ? '1px' : undefined,
      }}
    >
      {finalAvatarUrl && (
        <AvatarImage
          src={finalAvatarUrl}
          alt={group.name || 'Group'}
        />
      )}
      <AvatarFallback
        className="text-xs font-medium"
        style={{
          backgroundColor: group.color ? `${group.color}20` : undefined,
          color: group.color || undefined,
        }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Component to render a group option with avatar and childcare info
 */
function GroupOptionContent({
  group,
  childcareName
}: {
  group: GroupData;
  childcareName?: string;
}) {
  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <GroupAvatar group={group} />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium truncate">{group.name}</span>
        {childcareName && (
          <span className="text-xs text-muted-foreground truncate">{childcareName}</span>
        )}
      </div>
    </div>
  );
}

/**
 * A multi-select component for choosing groups.
 * Fetches groups using the useGroups hook and displays them in a multi-select dropdown
 * with avatars and childcare information.
 */
export function GroupMultiSelect({
  value,
  onChange,
  disabled,
  id,
  'aria-describedby': ariaDescribedBy,
  allowedGroupIds,
}: GroupMultiSelectProps) {
  const { i18n } = useLingui();
  const { data: groups, isLoading: isLoadingGroups, isError: isGroupsError } = useGroups(100, 0);
  const { data: childcaresData, isLoading: isLoadingChildcares } = useChildcares({ limit: 100, offset: 0 });

  const isLoading = isLoadingGroups || isLoadingChildcares;

  // Create a map of childcare_id to childcare name
  const childcareMap = useMemo(() => {
    const map = new Map<string, string>();
    const childcares = (childcaresData?.data || []) as Childcare[];
    childcares.forEach((childcare) => {
      if (childcare.id && childcare.name) {
        map.set(childcare.id, childcare.name);
      }
    });
    return map;
  }, [childcaresData]);

  // Create a map of group id to full group data
  // Note: groups is already the array from useGroups
  const groupMap = useMemo(() => {
    const map = new Map<string, GroupData>();
    const groupArray = groups || [];
    groupArray.forEach((group) => {
      if (group.id) {
        map.set(group.id, group);
      }
    });
    return map;
  }, [groups]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-muted-foreground text-sm">
          {i18n._({ id: 'common.loading', message: 'Loading...' })}
        </span>
      </div>
    );
  }

  if (isGroupsError) {
    return (
      <div className="flex items-center h-10 px-3 border border-destructive rounded-md bg-destructive/10">
        <span className="text-destructive text-sm">
          {i18n._({ id: 'shifts.form.groupLoadError', message: 'Failed to load groups' })}
        </span>
      </div>
    );
  }

  // groups is already the array from useGroups
  const groupList = groups || [];

  // Transform groups to EnhancedSelectOption format
  // Filter out groups without valid IDs to prevent empty string selections
  const options: EnhancedSelectOption[] = groupList
    .filter((group) => group.id && group.id.trim() !== '')
    .filter((group) => !allowedGroupIds || allowedGroupIds.includes(group.id!))
    .map((group) => ({
      value: group.id!,
      label: group.name || '',
      color: group.color || undefined,
    }));

  // Custom render function for options with avatar and childcare info
  const renderOption = (option: EnhancedSelectOption) => {
    const group = groupMap.get(option.value);
    if (!group) {
      return <span>{option.label}</span>;
    }
    const childcareName = group.childcare_id ? childcareMap.get(group.childcare_id) : undefined;
    return <GroupOptionContent group={group} childcareName={childcareName} />;
  };

  return (
    <EnhancedMultiSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={i18n._({
        id: 'shifts.form.selectGroups',
        message: 'Select groups (optional)',
      })}
      disabled={disabled}
      id={id}
      aria-describedby={ariaDescribedBy}
      renderOption={renderOption}
    />
  );
}
